// ============================================
// src/supabaseClient.js
// Sasha Learns — Supabase client setup
// ============================================

import { createClient, isAuthError, isAuthApiError, isAuthSessionMissingError, isAuthRetryableFetchError, AuthRetryableFetchError } from '@supabase/supabase-js'
import { captureSupabaseError, captureMetadataWriteError } from './lib/sentry'
import { ambientSessionMetadata } from './lib/engine/sessionMetadata'
import { buildConsentType, VPC_METHOD, isSignedMethod, isCompleteSignature, SCHOOL_DIRECT_CONSENT_TYPE } from './App/consentGate.js'
import { schoolConsentType } from './App/schoolConsentGate.js'
import { receiptConsentType, classPracticeConsentType } from './App/receiptConsentGate.js'
import { assignConsentType } from './App/assignConsentGate.js'
import { clearPendingJoinCode } from './lib/teacher/pendingJoinCode.ts'
import { clearPendingClaimCode } from './lib/teacher/pendingClaimCode'
import { clearPendingTeacherStart } from './lib/teacher/pendingTeacherStart.ts'
import { isCircuitOpen, recordSuccess, recordFailure } from './lib/engine/networkCircuitBreaker'
import { normalizeAssignedQueue, mergeCloudAssignedSkills } from './lib/engine/assignedSkills.ts'
import { normalizeSuppressedSet } from './lib/engine/suppressedSkills.ts'

// MA-377: retry policy for saveSession, matching attemptLogger's logAttempt.
const SESSION_MAX_RETRIES = 3
const SESSION_RETRY_BASE_MS = 150

export const SUPABASE_URL = 'https://vnhlizvgapsvycmktbtu.supabase.co'
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZuaGxpenZnYXBzdnljbWt0YnR1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcxODAwMzYsImV4cCI6MjA5Mjc1NjAzNn0.5n4Lq7pk8aOrldM4s9fk1aMZEgEUPuqdpEnbyyoDTwQ'

// PKCE flow opt-in. supabase-js v2's createClient default is 'implicit' which
// puts auth tokens in URL fragment (#access_token=...); Chrome's recent
// cross-origin-redirect fragment-stripping (privacy hardening) drops the
// fragment, breaking OAuth on Mac browsers while iOS Safari (preserves
// fragments) kept working. PKCE puts the code in ?code= query string which
// all browsers preserve. Diagnosed 2026-05-31 via Chrome MCP probe — see
// task #18 in Alex's tracker for the full diagnostic trail.
// P0 (Batch 1.5) — every request gets an 8s AbortController timeout so a
// hung socket on flaky/captive wifi RELEASES instead of pinning a connection
// until the browser's own (much longer) timeout. Without this, dozens of
// stuck requests exhausted the socket pool (ERR_INSUFFICIENT_RESOURCES).
const REQUEST_TIMEOUT_MS = 8000
const timeoutFetch = (input, init = {}) => {
  // Respect a caller-provided signal by not clobbering it when present.
  if (init.signal) return fetch(input, init)
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
  return fetch(input, { ...init, signal: controller.signal }).finally(() =>
    clearTimeout(timer),
  )
}

// Lazy singleton: defer createClient (and its RealtimeClient WebSocket setup)
// until the first actual network call. This lets Node.js test files import any
// module that transitively imports supabaseClient.js without triggering the
// WebSocket connection that fails on Node < 22. In production (browser) the
// first auth/from/rpc call triggers createClient on the same tick, so there is
// no observable difference.
// MA-779 — ⚠️AN AUTH-SERVER OUTAGE ON THE TOKEN REFRESH MUST NOT SIGN THE
// PARENT OUT. auth-js (`_callRefreshToken`, `_recoverAndRefresh`) removes the
// stored session and emits SIGNED_OUT for EVERY refresh error except its
// `AuthRetryableFetchError` (no Response at all, or 502/503/504/520–524/530).
// So a plain 500, a 501, an HTML 500, a 429 rate limit, or the API GATEWAY's
// code-less 401/403 (a revoked or rotated anon key, `{"message":"Invalid API
// key"}`) from `POST /auth/v1/token?grant_type=refresh_token` — the
// infrastructure failing, not the refresh token being refused (GoTrue refuses
// with a CODED 4xx: `refresh_token_not_found`, `refresh_token_already_used`,
// …) — signed a returning parent out at boot, or mid-session when the 30 s
// auto-refresh tick hit it, and sent the child's device to the sign-in screen
// (MA-759 RED NIT-1; MA-779 RED MUST-2 for the gateway 401, the same rule as
// isNoSessionAuthError applies to /auth/v1/user).
// This wrapper re-labels exactly those answers, and only for the refresh grant,
// as a 503 so auth-js takes its existing network-failure path instead: the
// session is KEPT and the next auto-refresh tick tries again. Every other
// answer, every other grant (password, PKCE, id_token) and every other
// endpoint pass through untouched, so a coded 4xx still signs the parent out
// exactly as before. The original status rides along in a header.
//
// COOLDOWN (MA-779 RED SHOULD-2): after a softened answer this device does not
// send another refresh for max(Retry-After, 30 s) (capped at 5 min); auth-js's
// own backoff retries inside that window are answered locally with the same
// synthetic 503. A rate limit is per IP, so without this every device behind
// a classroom NAT would keep hammering the limited endpoint (~16 requests a
// minute each, measured) and keep the limit tripped.
//
// A boot that waits on this retrying refresh is bounded by authResolve.js's
// AUTH_BOOT_TIMEOUT_MS (MA-779 RED MUST-1): auth-js holds its lock while it
// retries, so without that bound boot would wait on the splash indefinitely.
const REFRESH_OUTAGE_RELABEL_STATUS = 503
export const REFRESH_COOLDOWN_FLOOR_MS = 30_000
const REFRESH_COOLDOWN_CAP_MS = 5 * 60_000
// auth-js already treats these as retryable (lib/fetch.js NETWORK_ERROR_CODES).
const AUTH_JS_RETRYABLE_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524, 530])

function isRefreshGrantRequest(input) {
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url
    const url = new URL(raw)
    return url.pathname.endsWith('/auth/v1/token') && url.searchParams.get('grant_type') === 'refresh_token'
  } catch {
    return false
  }
}

/** A 401/403 GoTrue did not author: its body names no error code (GoTrue's
 *  versioned `code` or legacy `error_code`), or is not JSON at all. */
async function isCodelessRejection(res) {
  try {
    const data = await res.clone().json()
    return !(data && typeof data === 'object' && (typeof data.code === 'string' || typeof data.error_code === 'string'))
  } catch {
    return true
  }
}

function retryAfterMs(value, nowMs) {
  if (!value) return 0
  const secs = Number(value)
  if (Number.isFinite(secs)) return Math.max(0, secs * 1000)
  const at = Date.parse(value)
  return Number.isFinite(at) ? Math.max(0, at - nowMs) : 0
}

/** MA-779 — wraps a fetch so an infrastructure answer to the refresh grant
 *  (a 5xx auth-js would not already retry, a 429, or a code-less 401/403)
 *  reaches auth-js as a 503, which it treats as a network failure and does
 *  not sign out on; then holds further refresh requests locally for the
 *  cooldown. `now` is injectable for tests. */
export function softenRefreshOutage(baseFetch, { now = () => Date.now() } = {}) {
  let cooldownUntil = 0
  let cooldownStatus = null
  const relabel = (body, sourceHeaders, originalStatus, fromCooldown) => {
    const headers = new Headers(sourceHeaders)
    headers.set('x-pencilwise-refresh-original-status', String(originalStatus))
    if (fromCooldown) headers.set('x-pencilwise-refresh-cooldown', '1')
    return new Response(body, { status: REFRESH_OUTAGE_RELABEL_STATUS, statusText: 'Service Unavailable', headers })
  }
  return async (input, init) => {
    const refresh = isRefreshGrantRequest(input)
    if (refresh && now() < cooldownUntil) {
      return relabel('{}', { 'content-type': 'application/json' }, cooldownStatus, true)
    }
    const res = await baseFetch(input, init)
    if (!refresh) return res
    const outage =
      ((res.status >= 500 && res.status <= 599) && !AUTH_JS_RETRYABLE_STATUSES.has(res.status)) ||
      res.status === 429 ||
      ((res.status === 401 || res.status === 403) && (await isCodelessRejection(res)))
    if (!outage) return res
    const t = now()
    const wait = Math.min(REFRESH_COOLDOWN_CAP_MS, Math.max(REFRESH_COOLDOWN_FLOOR_MS, retryAfterMs(res.headers.get('retry-after'), t)))
    cooldownUntil = t + wait
    cooldownStatus = res.status
    return relabel(res.body, res.headers, res.status, false)
  }
}

function isAuthApiRequest(input) {
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url
    return new URL(raw).pathname.includes('/auth/v1/')
  } catch {
    return false
  }
}

/** MA-779 — makes GoTrue's error code reach auth-js in a BROWSER too.
 *  GoTrue answers a versioned request (auth-js always sends
 *  `X-Supabase-Api-Version: 2024-01-01`) with `{"code":"bad_jwt","message":…}`,
 *  and auth-js only reads that `code` when it can read the response's
 *  `x-supabase-api-version` header; otherwise it falls back to the legacy
 *  `error_code` field (lib/fetch.js handleError). A cross-origin response
 *  header is only readable when the server lists it in
 *  Access-Control-Expose-Headers, so whether `error.code` is set would hang on
 *  a CORS detail of the auth server. isNoSessionAuthError now NEEDS that code
 *  to tell GoTrue's rejection from the gateway's, so for every 4xx JSON answer
 *  from `/auth/v1/` that carries a string `code` and no `error_code`, this
 *  copies the code into `error_code`. auth-js then reads the same code on
 *  either branch. Nothing else in the response changes; a body without a
 *  string `code` (the gateway's `{"message":"Invalid API key"}`, GoTrue's
 *  legacy numeric `code`) is passed through untouched. */
export function mirrorGoTrueErrorCode(baseFetch) {
  return async (input, init) => {
    const res = await baseFetch(input, init)
    if (res.status < 400 || res.status > 499 || !isAuthApiRequest(input)) return res
    let data
    try {
      data = await res.clone().json()
    } catch {
      return res
    }
    if (!data || typeof data !== 'object' || typeof data.code !== 'string' || data.code === '' || 'error_code' in data) {
      return res
    }
    const headers = new Headers(res.headers)
    headers.delete('content-length')
    return new Response(JSON.stringify({ ...data, error_code: data.code }), {
      status: res.status,
      statusText: res.statusText,
      headers,
    })
  }
}

// ── Refresh-token revocation (MA-835 placeholder; RED probes of MA-779 #2466) ──
// auth-js 2.106.2 keeps refreshing a stored session's token in the BACKGROUND
// (boot recovery, the 30 s tick, every getSession/getUser, and an abandoned
// signOut that refreshes first), each round retrying ~25 s, and saves whatever
// a refresh returns. Two consequences this guard closes:
//   1. After a forced local sign-out (below) the abandoned round could refresh
//      the OLD token once the server recovers and silently sign the parent back in.
//   2. After the 10 s boot timeout the old parent's expired session stays
//      stored, and email-OTP `verifyOtp` saves a NEW parent's session without
//      taking the auth lock; the old parent's next refresh retry then
//      overwrote the new parent's session.
// So the client keeps a CLIENT-LIFETIME set of revoked refresh tokens. A token
// is revoked (a) when a local sign-out drops it and (b) when a new session is
// obtained by a sign-in grant (token/pkce/password/id_token, verify, signup):
// at that moment the token still STORED (the older user's) and any refresh
// token in flight are revoked. Any refresh carrying a revoked token is refused
// BEFORE the network with a synthetic retryable 503, and a result that arrives
// for a token revoked while it was in flight is discarded. The refusal is a
// 503, never a 400: a non-retryable refresh error makes auth-js remove
// WHATEVER session is stored, which by then is the new parent's.
// A boot-timeout sign-back-in (TOKEN_REFRESHED, nobody else signed in) is
// untouched: nothing revokes that token.
// CROSS-TAB (RED r1): auth storage is shared by every tab but this module's
// state is per tab, so tab 2's pending refresh of the old token could re-save the
// signed-out parent. Revocations are therefore also written to a small shared
// localStorage list (`pw-revoked-refresh-v1`) as SHA-256 hashes (never the token
// itself), checked by every tab before the network and before accepting an
// in-flight result. Entries expire after a day and the list is capped.
export const REVOKED_STORE_KEY = 'pw-revoked-refresh-v1'
export const REVOKED_TTL_MS = 24 * 60 * 60_000
export const REVOKED_MAX_ENTRIES = 50

function defaultRevokedStorage() {
  try { return typeof localStorage !== 'undefined' ? localStorage : null } catch { return null }
}

export function createRefreshGuard({ revokedStorage = defaultRevokedStorage(), now = () => Date.now() } = {}) {
  return {
    revoked: new Set(), // this tab's raw tokens (memory only)
    inflight: new Map(), // refresh token -> concurrent requests
    revokedStorage,
    now,
    readStoredRefreshToken: async () => null, // bound to the client by createAppClient
  }
}

/** Non-reversible digest of a refresh token: SHA-256 hex, or (no crypto.subtle) a
 *  pair of 32-bit FNV/djb2 hashes. Only this is ever persisted. */
export async function hashRefreshToken(token) {
  try {
    const subtle = globalThis.crypto?.subtle
    if (subtle) {
      const buf = await subtle.digest('SHA-256', new TextEncoder().encode(token))
      return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('')
    }
  } catch { /* fall through */ }
  let h1 = 0x811c9dc5, h2 = 5381
  for (let i = 0; i < token.length; i++) {
    h1 = Math.imul(h1 ^ token.charCodeAt(i), 0x01000193) >>> 0
    h2 = (Math.imul(h2, 33) + token.charCodeAt(i)) >>> 0
  }
  return `fnv${h1.toString(16)}${h2.toString(16)}`
}

function readRevokedList(guard) {
  try {
    const raw = guard.revokedStorage?.getItem(REVOKED_STORE_KEY)
    const list = raw ? JSON.parse(raw) : []
    const t = guard.now()
    return Array.isArray(list) ? list.filter((e) => e && typeof e.h === 'string' && typeof e.t === 'number' && t - e.t < REVOKED_TTL_MS) : []
  } catch { return [] }
}

export async function revokeRefreshToken(guard, token) {
  if (!token) return
  guard.revoked.add(token)
  const h = await hashRefreshToken(token)
  try {
    if (!guard.revokedStorage) return
    const list = readRevokedList(guard).filter((e) => e.h !== h)
    list.push({ h, t: guard.now() })
    guard.revokedStorage.setItem(REVOKED_STORE_KEY, JSON.stringify(list.slice(-REVOKED_MAX_ENTRIES)))
  } catch { /* storage full/blocked: this tab still holds it in memory */ }
}

export async function isRefreshTokenRevoked(guard, token) {
  if (!token) return false
  if (guard.revoked.has(token)) return true
  if (!guard.revokedStorage) return false
  const h = await hashRefreshToken(token)
  return readRevokedList(guard).some((e) => e.h === h)
}

function refreshTokenOf(init) {
  try {
    const o = typeof init?.body === 'string' ? JSON.parse(init.body) : null
    return o && typeof o.refresh_token === 'string' ? o.refresh_token : null
  } catch { return null }
}

function revokedRefreshResponse() {
  return new Response(JSON.stringify({ code: 'refresh_token_revoked', error_code: 'refresh_token_revoked', msg: 'Refresh token revoked by a local sign-out or a newer sign-in' }), {
    status: REFRESH_OUTAGE_RELABEL_STATUS,
    statusText: 'Service Unavailable',
    headers: { 'content-type': 'application/json', 'x-pencilwise-refresh-revoked': '1' },
  })
}

function isSessionGrantRequest(input, init) {
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url
    const u = new URL(raw)
    const method = (init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase()
    if (u.pathname.endsWith('/auth/v1/token')) return u.searchParams.get('grant_type') !== 'refresh_token'
    return method === 'POST' && (u.pathname.endsWith('/auth/v1/verify') || u.pathname.endsWith('/auth/v1/signup'))
  } catch { return false }
}

export async function revokeStoredRefreshToken(guard, exceptToken = null) {
  let stored = null
  try { stored = await guard.readStoredRefreshToken() } catch { /* unreadable: nothing to revoke */ }
  if (stored && stored !== exceptToken) await revokeRefreshToken(guard, stored)
}

export function guardRefreshTokens(baseFetch, guard) {
  return async (input, init) => {
    const refresh = isRefreshGrantRequest(input)
    const token = refresh ? refreshTokenOf(init) : null
    if (token && (await isRefreshTokenRevoked(guard, token))) return revokedRefreshResponse() // never reaches the network
    if (token) guard.inflight.set(token, (guard.inflight.get(token) || 0) + 1)
    let res
    try {
      res = await baseFetch(input, init)
    } finally {
      if (token) {
        const n = (guard.inflight.get(token) || 1) - 1
        if (n <= 0) guard.inflight.delete(token); else guard.inflight.set(token, n)
      }
    }
    if (refresh) return token && (await isRefreshTokenRevoked(guard, token)) ? revokedRefreshResponse() : res
    if (res.ok && isSessionGrantRequest(input, init)) {
      // A NEW session was just granted (not yet saved): the older user's token is
      // still the stored one. Revoke it and every refresh still in flight.
      let newToken = null
      try { newToken = (await res.clone().json())?.refresh_token ?? null } catch { /* no session body */ }
      if (typeof newToken === 'string' && newToken) { // only a grant that actually carries a session
        await revokeStoredRefreshToken(guard, newToken)
        for (const t of [...guard.inflight.keys()]) if (t !== newToken) await revokeRefreshToken(guard, t)
      }
    }
    return res
  }
}

/** The options the app's one Supabase client is built with. Exported so the
 *  MA-779 real-client test builds its client from the SAME options (plus its
 *  own in-memory storage) instead of a copy that could drift. */
export function supabaseClientOptions(baseFetch = timeoutFetch, { now, guard = createRefreshGuard() } = {}) {
  return {
    auth: { flowType: 'pkce' },
    global: { fetch: guardRefreshTokens(softenRefreshOutage(mirrorGoTrueErrorCode(baseFetch), now ? { now } : undefined), guard) },
  }
}

/** The app's client recipe: the options above plus the guard bound to the
 *  client's own storage (the guard reads the stored refresh token from it).
 *  `authOptions`/`baseFetch`/`now` are for tests; production passes none. */
export function createAppClient(url, key, { baseFetch = timeoutFetch, authOptions = {}, now, revokedStorage } = {}) {
  const guard = createRefreshGuard(revokedStorage === undefined ? {} : { revokedStorage })
  const opts = supabaseClientOptions(baseFetch, { now, guard })
  const client = createClient(url, key, { ...opts, auth: { ...opts.auth, ...authOptions } })
  guard.readStoredRefreshToken = async () => {
    const a = client.auth
    const raw = await a.storage.getItem(a.storageKey)
    const stored = typeof raw === 'string' ? JSON.parse(raw) : raw
    return stored && typeof stored.refresh_token === 'string' ? stored.refresh_token : null
  }
  client.__pwRefreshGuard = guard
  return client
}

let _supabaseInstance = null
function lazySupabase() {
  if (!_supabaseInstance) {
    _supabaseInstance = createAppClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  }
  return _supabaseInstance
}
/** @type {any} */
export const supabase = new Proxy({}, {
  get(_, prop) { return lazySupabase()[prop] },
})

// ─── Auth helpers ───────────────────────────

export async function signInWithGoogle() {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin,
    },
  })
  if (error) throw error
  return data
}

// MA-289 slice (b) — passwordless email sign-in (magic link / OTP), gated
// in the UI behind isEmailAuthEnabled (src/lib/emailAuthFlag.ts) until the
// founder enables the Email provider in the Supabase dashboard. `role` is
// stamped into the redirect URL's `role` query param — a magic link opens
// in a FRESH browser context (mail app, maybe a different device), so it
// can't rely on the tap-time localStorage write the way Google's same-tab
// OAuth round-trip can; routes/index.tsx reads it back via
// roleOverrideFromSearch to restamp pw_user_role on landing.
export async function signInWithEmailOtp(email, role) {
  const { data, error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: `${window.location.origin}/?role=${encodeURIComponent(role)}`,
    },
  })
  if (error) throw error
  return data
}

// `supabase.auth.signOut()` with NO argument defaults to `scope: 'global'` in
// supabase-js v2 (verified against @supabase/supabase-js ^2.106.2 and the
// official auth-signout reference) — it revokes EVERY session for the user,
// i.e. it signs the user out on all devices. This is intentional and matches
// the "Sign out of all devices" button (src/screens/ParentSettingsSections.tsx).
// Do NOT add `{ scope: 'local' }` here without also relabeling that button.
// (Backlog MA-13 — honest sign-out label; verified, behaviour already correct.)
/** How long the global sign-out may take before the local escape hatch runs. */
export const SIGN_OUT_GRACE_MS = 4000

/** MA-835 placeholder — a local sign-out that needs no server. auth-js's own
 *  signOut refreshes an expired token FIRST and, when that refresh fails
 *  retryably (a softened outage, a network failure), returns BEFORE removing
 *  the session (and holds the auth lock for the whole ~25 s backoff round), so
 *  under a refresh outage Sign out could never finish. Revoke the dropped
 *  refresh token (so the abandoned round cannot re-save it when the server
 *  recovers), then clear storage and fire SIGNED_OUT through auth-js. */
async function forceLocalSignOut(client) {
  const a = client.auth
  if (client.__pwRefreshGuard) await revokeStoredRefreshToken(client.__pwRefreshGuard)
  try {
    if (typeof a._removeSession === 'function') await a._removeSession()
    else if (a.storage && a.storageKey) await a.storage.removeItem(a.storageKey)
  } catch (e) { console.error(e) }
}

export async function signOut(client = supabase, storage) {
  let error = null
  let timer
  try {
    const res = await Promise.race([
      client.auth.signOut(),
      new Promise((resolve) => {
        timer = setTimeout(() => resolve({ error: new AuthRetryableFetchError('Sign-out timed out', 0) }), SIGN_OUT_GRACE_MS)
      }),
    ])
    error = res?.error ?? null
  } catch (e) { error = e } finally { clearTimeout(timer) }
  if (error) {
    // A sign-out that could not REACH or FINISH with the server (network failure,
    // a softened refresh outage, a timeout) still signs this device out. Any
    // other error keeps the old contract: throw, leave the session.
    if (!isAuthRetryableFetchError(error)) throw error
    await forceLocalSignOut(client)
  }
  // Shared/kiosk-device residue: a scanned join code (MA-238) is tab-scoped
  // sessionStorage, not user-scoped — left uncleared, it would misattribute
  // Parent A's scan to Parent B on a sign-out/sign-in handoff on the same tab.
  clearPendingJoinCode(storage)
  // MA-291: the per-student CLAIM code is the same class of residue with a
  // sharper edge (it links a child) — cleared on the same boundary.
  clearPendingClaimCode(storage)
  // MA-1056: a pending /teacher/start entry (localStorage, first party) is
  // device residue of the same kind — "Try a different account" on the beta
  // gate must not hand it to whoever signs in next.
  clearPendingTeacherStart()
}

// ─── Student helpers ────────────────────────

// MA-288 kiosk shadow-student exclusion — THE line that answers 0023's own
// red-team objection: a kiosk-armed device's create_kiosk_student() RPC
// (migration 0042) inserts a `students` row with `parent_id = auth.uid()`
// (the TEACHER's own auth user) so the classroom `child_attempts` write can
// use the ordinary parent-scoped RLS policy — but that means a shadow row
// is, by every other filter this function already applies, indistinguishable
// from one of the teacher's OWN family kids. `enrollment_id IS NOT NULL` is
// exactly and only true for a kiosk shadow (0042's own column comment), so
// excluding it here is the actual fix; 0042's UNIQUE + ON DELETE CASCADE on
// that column is the belt-and-braces backstop for the one way this filter
// alone could be defeated (a shadow surviving its enrollment with
// enrollment_id nulled back out — the cascade makes that state impossible).
// MA-629 §3a — deadline-aware clamp for the owned-student probe below.
// `performance.now()`, NOT `Date.now()`: the outer race this budget must fit
// inside (`withTimeout` in useCloudSync.js, a `setTimeout` deadline) runs on
// the runtime's MONOTONIC timer, immune to clock changes. Guarding it with a
// wall clock re-opens the hazard a backward Date.now() jump (NTP resync,
// leaving airplane mode, a manual time change — all common on the mobile
// devices this population uses) would move the clamp's `total ≤
// FETCH_STUDENTS_SELF_DEADLINE_MS` bound backward too, extending the real
// elapsed time past it and pushing the outer race to fire. onboardingHoldGuard
// already treats clock skew as live (its own future-created_at guard) — this
// is the same discipline applied to a timer instead of a timestamp.
export const OWNED_PROBE_TIMEOUT_MS = 3000;
export const FETCH_STUDENTS_SELF_DEADLINE_MS = 7000;
export const PROBE_MIN_USEFUL_MS = 250;

// MA-629 §3a — tri-state coercion for the owned-student probe's `count`.
// postgrest-js parses `count` out of the content-range response header; a
// missing header leaves it `null` and a `*` denominator yields `NaN` — BOTH
// with `error === null`. `(count ?? 0)` is forbidden here: it fails OPEN to
// "owns none", the wrong direction for a guard that must fail closed (keep
// holding) on anything it cannot prove.
function coerceOwnedCount(count) {
  return Number.isInteger(count) ? count > 0 : null;
}

// MA-629 §3a — the owned-student probe itself: a head-only exact count,
// owner-scoped and (except on the dead pre-0042 42703 fallback) family-scoped
// exactly like fetchStudents' main query, deliberately WITHOUT the
// `is_active` filter — that omission is the whole point, since this probe
// exists to answer "did this parent ever own a family row" independent of
// whether it is currently active. Never propagates a failure: a thrown error,
// a returned `{error}`, or a timeout against `budgetMs` all resolve `null`
// (unknown -> the guard keeps holding), so a slow or broken probe can never
// turn a successful-but-empty fetch into a rejected one.
async function probeHasAnyOwnedStudent(client, ownerId, budgetMs, { familyScoped }) {
  if (!(budgetMs > PROBE_MIN_USEFUL_MS)) return null;
  let builder = client
    .from('students')
    .select('id', { count: 'exact', head: true })
    .eq('parent_id', ownerId);
  if (familyScoped) builder = builder.is('enrollment_id', null);
  // MA-629 A1 — a probe that blows its budget must not merely be IGNORED by
  // this function: a bare `Promise.race` against a `setTimeout` leaves that
  // timer running on the event loop for the rest of its nominal budget even
  // after the race has already settled (measured: a race that settles at
  // 0ms still held the process open until ~budgetMs later), and — worse —
  // the underlying network request keeps running with no signal telling it
  // to stop. That is exactly the "dozens of stuck requests exhausted the
  // socket pool" hazard `REQUEST_TIMEOUT_MS`/`timeoutFetch` above already
  // exists to guard against, except this call would silently ride THAT
  // ~8s ceiling instead of its own, much shorter one — on precisely the slow
  // device this clamp exists for. `abortSignal` gives a timed-out probe a
  // way to actually STOP the request: real postgrest-js RESOLVES (does not
  // reject) with an AbortError in `error` once aborted
  // (`@supabase/postgrest-js/dist/index.cjs:997`), so the existing
  // `if (error) return null` below handles it for free — the listener-based
  // race arm is a pure backstop for a client that ignores the signal
  // entirely, not the primary path. `finally` clears the local timer on
  // every exit so a probe that settles on its own never leaves a stray timer
  // pinning the event loop for the remainder of its budget.
  //
  // ⭐The controller and the `abortSignal` chain live INSIDE the try,
  // deliberately. Constructing them outside it would let a client whose
  // `abortSignal` THROWS reject this function — and therefore `fetchStudents`,
  // and therefore `refreshStudents`, which sets `cloudError` and hands the
  // parent the cloud-error hold on a fetch that actually SUCCEEDED-empty. That
  // is the exact net-negative outcome this whole clamp exists to prevent,
  // reached through a different door, and it would break the never-propagates
  // contract stated ten lines above. Real postgrest-js cannot throw here
  // (`dist/index.cjs:997` — `abortSignal(signal) { this.signal = signal; return this }`),
  // so it is unreachable through the production client; it IS reachable
  // through the DI seam and through any future postgrest change, which is
  // reason enough for two lines on this path. The chain RESULT is guarded for
  // the same reason: a builder whose `abortSignal` returned nothing would
  // otherwise become `undefined`, and the probe would silently never run —
  // resolving `null` with no error anywhere, which is safe in direction but is
  // exactly the undetectable-failure class this probe is otherwise careful
  // about.
  let timer;
  try {
    const controller = new AbortController();
    if (typeof builder.abortSignal === 'function') {
      const chained = builder.abortSignal(controller.signal);
      if (chained) builder = chained;
    }
    timer = setTimeout(() => controller.abort(), budgetMs);
    const result = await Promise.race([
      Promise.resolve(builder),
      new Promise((resolve) => {
        controller.signal.addEventListener(
          'abort',
          () => resolve({ __probeTimedOut: true }),
          { once: true },
        );
      }),
    ]);
    if (result && result.__probeTimedOut) return null;
    const { count, error } = result || {};
    if (error) return null;
    return coerceOwnedCount(count);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// MA-629 §3a — resolves the tri-state `hasAnyOwnedStudent` for one fetch
// outcome. `rows.length > 0` is itself proof (no second round-trip); a truly
// empty result runs the probe against whatever budget remains of
// `FETCH_STUDENTS_SELF_DEADLINE_MS`, clamped to at most `OWNED_PROBE_TIMEOUT_MS`
// — the `Math.min` is load-bearing: without it, a probe started late in a slow
// boot would still run its full nominal cap and blow the outer envelope.
async function resolveHasAnyOwnedStudent(client, ownerId, rows, t0, { familyScoped }) {
  if (rows.length > 0) return true;
  const elapsed = performance.now() - t0;
  const probeBudget = Math.min(OWNED_PROBE_TIMEOUT_MS, FETCH_STUDENTS_SELF_DEADLINE_MS - elapsed);
  return probeHasAnyOwnedStudent(client, ownerId, probeBudget, { familyScoped });
}

// MA-759 — the statuses with which GoTrue REJECTS a token or a session (bad,
// expired, revoked or malformed JWT; a refresh token it refuses). Anything
// else from /auth/v1/user — 5xx, 429, a non-JSON body, a network failure — is
// the auth server failing, not "this device has no session".
const REJECTED_TOKEN_STATUSES = new Set([400, 401, 403, 422])

/** MA-759 — true ONLY for an auth error that means "there is no valid session"
 *  (so fetchStudents may return its owner-less empty result): a missing or
 *  revoked session (`AuthSessionMissingError`, incl. GoTrue's
 *  `session_not_found`), auth-js's own invalid-JWT error, or a GoTrue API
 *  rejection with status 400/401/403/422 THAT NAMES ITS REASON (MA-779, see
 *  below). Every other value — a 5xx or 429 `AuthApiError`, a code-less
 *  401/403, `AuthUnknownError`, `AuthRetryableFetchError`, anything
 *  unrecognised — is false, so the caller surfaces it as a cloud error.
 *
 *  MA-779 — ⚠️A 401 FROM THE API GATEWAY IS NOT GOTRUE REJECTING THE TOKEN.
 *  Requests reach GoTrue through Supabase's API gateway, which answers on its
 *  own when the project's API key is wrong: a revoked, rotated or disabled
 *  anon key gets `401 {"message":"Invalid API key","hint":…}` for EVERY
 *  request. That body has no error code. Read as "no session", it sent every
 *  returning parent down the owner-less path to "We couldn't find your
 *  child's profile" — for an outage that is ours. GoTrue itself names every
 *  rejection (`bad_jwt`, `no_authorization`, `user_not_found`,
 *  `validation_failed`, …), and auth-js copies that name into `error.code`
 *  from either the versioned `code` or the legacy `error_code` field
 *  (lib/fetch.js handleError; mirrorGoTrueErrorCode makes sure it can in a
 *  browser too). So a rejection status without a code is
 *  treated as the infrastructure failing: a cloud error with Retry and Sign
 *  out, never the profile-missing copy. */
export function isNoSessionAuthError(error) {
  if (isAuthSessionMissingError(error)) return true
  // No `isAuthInvalidJwtError` helper exists in auth-js 2.106; the class's
  // name is the same test the exported helpers use. Status 400.
  if (isAuthError(error) && error.name === 'AuthInvalidJwtError') return true
  return (
    isAuthApiError(error) &&
    REJECTED_TOKEN_STATUSES.has(error.status) &&
    typeof error.code === 'string' &&
    error.code.length > 0
  )
}

export async function fetchStudents(client = supabase) {
  // MA-629 §3a — stamped at entry; every deadline below is measured against
  // this, never a wall clock (see OWNED_PROBE_TIMEOUT_MS's comment).
  const t0 = performance.now();
  // MA-579 — OWNER-SCOPE THIS EXPLICITLY. RLS is not a substitute.
  //
  // `students_select` is `parent_id = auth.uid() OR id IN (student_access
  // WHERE granted_to = auth.uid())`. That policy answers "MAY you see this
  // row?" — it does not answer "is this row YOURS?", and the family kid
  // switcher needs the second question. A teacher is granted `student_access`
  // on every student claimed into their class (claim_class_enrollment,
  // migration 0026/0043), so without an explicit filter every such child came
  // back here indistinguishable from the teacher's own kids, was adopted into
  // the local registry by autoLinkByNameAge (a NAME-only match), and appeared
  // in the family kid picker.
  //
  // ⭐FOUND THE HARD WAY (founder, live, 2026-08-27): picking one of those
  // kids to redeem a class code failed with `not authorized: caller does not
  // own the given student`, because claim_class_enrollment's ownership check
  // is the strict `parent_id = auth.uid()`. The picker offered a child the
  // claim path could never accept. This is the client-side twin of MA-576,
  // which closed the same hole in the outbound email pipeline.
  const { data: auth, error: authError } = await client.auth.getUser()
  // MA-751 — ⚠️A NETWORK FAILURE IS NOT "NO SESSION". auth-js RESOLVES (does
  // not throw) `{user:null, error}` for EVERY auth error, including
  // `AuthRetryableFetchError`: a fetch that never got a Response (offline,
  // DNS, the 8s timeoutFetch abort) or a 502/503/504/52x/530 gateway error
  // (`@supabase/auth-js` lib/fetch.js handleError). Left to fall through, that
  // outage took the no-owner branch below and returned a SUCCESSFUL empty
  // result, so useCloudSync never set cloudError/cloudOffline and a returning
  // parent on a fresh device saw "We couldn't find your child's profile"
  // instead of "We couldn't load your progress … check your connection".
  //
  // MA-759 — ⚠️AND NEITHER IS A 5xx OR A 429. MA-751 rethrew only that one
  // retryable class, so a plain 500/501 (`AuthApiError`), a 500 whose body is
  // an HTML error page (`AuthUnknownError`, no status) and a 429 rate limit
  // still fell through to the owner-less success and showed a returning parent
  // "We couldn't find your child's profile". The rule is now an ALLOW-list:
  // stay owner-less ONLY when there really is no valid session (see
  // isNoSessionAuthError); EVERY other auth error is rethrown, and
  // refreshStudents' catch turns it into cloudError + cloudOffline exactly like
  // a students-query 500. The no-session (AuthSessionMissingError — the ?dev=1
  // default) and rejected-token (400/401/403/422) paths still reach the
  // owner-less return unchanged.
  if (authError && !isNoSessionAuthError(authError)) throw authError
  const ownerId = auth?.user?.id
  // MA-581 — ⚠️RETURN THE OWNER WE ACTUALLY QUERIED, and null when there was
  // none. getUser() RESOLVES with {user:null} on a missing session
  // (AuthSessionMissingError is returned, not thrown), so a bare `return []`
  // sends a SESSION FAILURE down the SUCCESS path. MA-581's consumers
  // intersect their local kid lists against this owned set, so an
  // empty-but-authoritative result would hide EVERY kid — and that is the
  // DEFAULT under ?dev=1, where useAuth stamps a local user with no Supabase
  // session. Reporting ownerId:null instead lets the caller mark the set
  // untrustworthy and fail OPEN.
  //
  // It also closes a race the React `user` state cannot: the Supabase session
  // flips to account B before `setUser` commits, so stamping the caller's
  // `user.id` could label B's rows as A's. This is the id the query really used.
  //
  // MA-629 §3a — no session means no owner to scope a probe to; the tri-state
  // is `null` (unknown), never `false`. A future consumer must not read a
  // no-session boot as positive proof of emptiness.
  if (!ownerId) return { rows: [], ownerId: null, hasAnyOwnedStudent: null }
  const { data, error } = await client
    .from('students')
    .select('*')
    .eq('parent_id', ownerId)
    .eq('is_active', true)
    .is('enrollment_id', null)
    .order('created_at', { ascending: true })
  if (!error) {
    const rows = data || []
    const hasAnyOwnedStudent = await resolveHasAnyOwnedStudent(client, ownerId, rows, t0, { familyScoped: true })
    return { rows, ownerId, hasAnyOwnedStudent }
  }
  // Pre-apply graceful degradation (the MA-59 missing-column convention,
  // adapted from a missing-function/table check to a missing-column one):
  // before migration 0042 lands, `enrollment_id` doesn't exist and
  // PostgREST 400s the whole request (42703) rather than just omitting the
  // filter — so on that exact failure, retry once WITHOUT it. Every other
  // error still throws unchanged.
  const isMissingEnrollmentIdColumn =
    error.code === '42703' || /enrollment_id/i.test(error.message || '')
  if (!isMissingEnrollmentIdColumn) throw error
  const { data: fallbackData, error: fallbackError } = await client
    .from('students')
    .select('*')
    .eq('parent_id', ownerId)
    .eq('is_active', true)
    .order('created_at', { ascending: true })
  if (fallbackError) throw fallbackError
  // MA-629 §3a — pre-0042 fallback: no `enrollment_id` column exists yet, so
  // the probe drops the family-scope filter too (kiosk shadows cannot exist
  // before 0042 creates them). Where this path instead fired for the message-
  // regex's OTHER reason (a real column + real shadows), the probe over-counts
  // — reading `true` and holding — which is the safe direction, not assumed.
  const fallbackRows = fallbackData || []
  const hasAnyOwnedStudent = await resolveHasAnyOwnedStudent(client, ownerId, fallbackRows, t0, { familyScoped: false })
  return { rows: fallbackRows, ownerId, hasAnyOwnedStudent }
}

export async function createStudent(parentId, student, client = supabase) {
  const { data, error } = await client
    .from('students')
    .insert({
      parent_id: parentId,
      name: student.name,
      age: student.age || null,
      grade: student.grade || 'Kindergarten',
      math_level_id: student.mathLevelId || 'math.11',
      reading_level_id: student.readingLevelId || 'reading.09',
    })
    .select()
    .single()
  if (error) {
    // Postgres 23505 = unique_violation. Migration 0013 adds a case-insensitive
    // (parent_id, lower(name)) unique index on students, so a race between two
    // concurrent creates (or a retry after a transient network failure that
    // actually committed) hits this code. Re-query for the existing row and
    // return it instead of failing — the caller gets a valid student either way.
    // This matches the CLAUDE.md §Student-create discipline rule: every
    // createStudent path MUST do a lookup-by-(parent_id, name) before inserting,
    // but when the INSERT itself races a concurrent write we handle it here.
    if (error.code === '23505') {
      // MA-629 slice b (docs/build/specs/MA-629b-name-collision-revive.md
      // §2a) — the lookup is now ACTIVE-AGNOSTIC (no `.eq('is_active', true)`
      // — that was the bug: a deactivated same-name row violates the same
      // index but was filtered out of the recovery's own result) AND
      // FAMILY-SCOPED (`.is('enrollment_id', null)` — mandatory, not belt-
      // and-braces: without it a kiosk shadow, which carries
      // `parent_id = auth.uid()` for the TEACHER per 0042, would be handed
      // to a family create path and, worse, could be revived out of the
      // retirement 0048 deliberately gave it).
      //
      // `ilike` is a wildcard match (`%`/`_`), so it can return MORE than
      // one row — `.single()` is gone and `.limit(1)` does NOT replace it:
      // picking by position can revive the WRONG child (see the spec's
      // BLOCK-2 worked example). The row that must win is the one that
      // actually caused the 23505 — chosen client-side by an EXACT
      // case-insensitive match against the constraint's own comparison.
      const { data: candidates, error: lookupError } = await client
        .from('students')
        .select('*')
        .eq('parent_id', parentId)
        .ilike('name', student.name)
        .is('enrollment_id', null)
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
      // ⭐ NO `.trim()` here — deliberately. `students_parent_name_unique`
      // (0013) is a unique index on `lower(name)`, NOT `trim(lower(name))`,
      // so "Sam%" and "Sam% " are two LEGAL rows under that index. A
      // trimming comparator would match both and put row selection back on
      // ordering — reintroducing BLOCK-2's wrong-row revive through a
      // different door (see the spec's R2-1). This comparator reproduces the
      // DATABASE CONSTRAINT, not the fuzzy in-memory dedup the two
      // pre-insert lookups (`App.jsx`, `useActiveStudentId.js`) intentionally
      // do with a trim — those are a different job.
      const sameName = (a, b) => String(a ?? '').toLowerCase() === String(b ?? '').toLowerCase()
      const existing = !lookupError && (candidates ?? []).find((r) => sameName(r.name, student.name))
      if (existing) {
        // MA-629 slice b §2b/§2c — an inactive match is REVIVED (via the
        // sanctioned reactivate_student RPC, migration 0052), not returned
        // as-is: returning it inert would hand the caller a row
        // `fetchStudents` filters out of every picker (the orphan shape §4
        // rejects). An active match is returned unchanged — today's
        // behaviour, unregressed.
        if (existing.is_active === false) {
          return reactivateStudent(existing.id, 'name_collision_revive', client)
        }
        return existing
      }
      // Lookup found nothing (or itself failed) — fall through to the
      // normal error path below.
    }
    captureSupabaseError('createStudent failed', error, { parentId })
    throw error
  }
  return data
}

// Optional columns that may not exist in older Supabase schemas. If the
// migration hasn't been applied, the request fails with PGRST204 ("column
// X of relation Y does not exist"); we strip the offending key and retry
// once. Keeps the app functional for users who haven't applied the latest
// schema while still letting up-to-date deployments use the column.
const OPTIONAL_STUDENT_COLUMNS = ['mascot_id', 'auto_read_enabled'];

export async function updateStudent(studentId, updates, client = supabase) {
  let payload = { ...updates };
  for (let attempt = 0; attempt < OPTIONAL_STUDENT_COLUMNS.length + 1; attempt++) {
    const { data, error } = await client
      .from('students')
      .update(payload)
      .eq('id', studentId)
      .select()
      .single();
    if (!error) return data;
    // Detect missing-column failures and retry without that key.
    const msg = String(error.message || '');
    const missing = OPTIONAL_STUDENT_COLUMNS.find((col) => msg.includes(`'${col}'`) || msg.includes(`"${col}"`));
    if (missing && missing in payload) {
      const { [missing]: _drop, ...rest } = payload;
      payload = rest;
      continue;
    }
    captureSupabaseError('updateStudent failed', error, { studentId });
    throw error;
  }
  // Should be unreachable — the loop either returns data or throws.
  throw new Error('updateStudent: exhausted retries');
}

// MA-39 — Guarded level (math_level_id/reading_level_id) writer.
// Same last-writer-wins shape as the (deleted, MA-287) streak writer, guarded
// on the new levels_updated_at column (migration 0025) instead of a domain date —
// levels have no natural "which value is newer" ordering, so the writing
// device stamps levelsUpdatedAt itself at the moment its local module id
// actually changed (placement finish / placement-skip seed / a parent's
// explicit level override), NOT at sync time (see the migration header for
// why "now, at sync time" would defeat the guard). Writes only when the
// stored levels_updated_at is null or not-newer than the incoming stamp.
// Returns the updated row, or null when the guard rejected the write (cloud
// already newer — treat as a no-op, matching the streak writer's contract).
export async function updateStudentLevelsSafe(studentId, { mathLevelId, readingLevelId, levelsUpdatedAt }, client = supabase) {
  if (!studentId || !levelsUpdatedAt) return null;
  const { data, error } = await client
    .from('students')
    .update({
      math_level_id: mathLevelId,
      reading_level_id: readingLevelId,
      levels_updated_at: levelsUpdatedAt,
    })
    .eq('id', studentId)
    .or(`levels_updated_at.is.null,levels_updated_at.lte.${levelsUpdatedAt}`)
    .select()
    .maybeSingle();
  if (error) {
    captureSupabaseError('updateStudentLevelsSafe failed', error, { studentId });
    throw error;
  }
  return data; // null = guard rejected (cloud already newer); row = written
}

// MA-395 — guarded parent-device writer for students.assigned_skill_ids.
// Mirrors updateStudentLevelsSafe's stamp-guarded CAS (migration 0038's
// assigned_skill_ids_updated_at), but this column has a SECOND writer the
// levels case doesn't: the teacher assign_skill_to_student RPC (migration
// 0027), which order-preserving-appends WITHOUT ever touching the stamp.
// A blind stamp-guarded overwrite of the whole array would silently drop a
// teacher's freshly-RPC-appended id if it landed between this device's last
// pull and this push (the RPC leaves no trace the stamp guard alone could
// see). So this writer:
//   1. Re-reads the row's CURRENT assigned_skill_ids immediately before
//      writing (scoped to that one column so this step still works even
//      pre-migration-apply, when assigned_skill_ids_updated_at doesn't
//      exist yet).
//   2. Diffs that fresh value against `cloudMirror` — this device's own
//      last-observed cloud snapshot (Progress.assignedSkillsCloudMirror,
//      refreshed on every prior pull/push). Anything present now but absent
//      from cloudMirror is presumed written by someone else since (a
//      teacher RPC append, or a race with another parent device) and is
//      unconditionally re-unioned into the array being written. cloudMirror
//      undefined (never observed a baseline — e.g. a toggle that races the
//      very first boot pull) degrades conservatively: EVERY current cloud
//      id is treated as unknown-origin and kept, so this device can never
//      be the one to silently delete something it has never seen.
//   3. Writes guarded by the stamp alone (rejects if cloud is genuinely
//      newer) — same shape as updateSuppressedSkillIdsSafe below.
//
// HISTORY (engine-truth-2026-09-08.md, E2 / MA-395): this writer used to
// ALSO carry a second guard, `.eq('assigned_skill_ids', freshRow?.assigned_skill_ids
// ?? [])`, meant as a TOCTOU belt-and-braces over step 1's read. It was
// dead on arrival: `assigned_skill_ids` is `jsonb`
// (supabase/migrations/0027_teacher_skill_assignment.sql:20), and
// postgrest-js's `.eq()` does no serialization for non-string values — it
// template-stringifies whatever you pass
// (node_modules/@supabase/postgrest-js/src/PostgrestFilterBuilder.ts:172,
// `eq.${value}`). A JS array becomes a bare comma join, e.g.
// `assigned_skill_ids=eq.ms.math.03` for `["ms.math.03"]`, or
// `assigned_skill_ids=eq.` for `[]` — never valid JSON, so PostgREST either
// 22P02s coercing the filter to the column's jsonb type or matches zero
// rows; it could never equal the real jsonb value. So the guarded UPDATE
// never landed a single write, on every device, in both directions, since
// this writer shipped — every parent-device skill assign/un-assign was a
// silent no-op (see supabaseClient.js:367-402's swallow-and-retry-forever
// path, and the two call sites' telemetry-only catches). The guard has been
// dropped; CAS is now on the stamp alone. This is safe *because* step 2's
// re-read/diff/re-union already re-unions any id present in the fresh cloud
// row but absent from cloudMirror (the teacher-RPC-append race this array
// guard was actually trying to catch) — the array guard was strictly
// belt-and-braces on top of that, and the only thing it additionally caught
// (a second writer landing in the exact instant between this call's own
// SELECT and its UPDATE) it never actually could catch, because it could
// never match in the first place. See engine-truth-2026-09-08.md §E2 Fix 1
// option (b).
export async function updateAssignedSkillIdsSafe(
  studentId,
  { assignedSkillIds, assignedSkillIdsUpdatedAt, cloudMirror },
  client = supabase,
) {
  if (!studentId || !assignedSkillIdsUpdatedAt) return null;
  const desired = normalizeAssignedQueue(assignedSkillIds);
  const known = normalizeAssignedQueue(cloudMirror); // undefined -> [] -> "keep everything current"

  const { data: freshRow, error: selectError } = await client
    .from('students')
    .select('assigned_skill_ids')
    .eq('id', studentId)
    .maybeSingle();
  if (selectError) {
    captureSupabaseError('updateAssignedSkillIdsSafe select failed', selectError, { studentId });
    throw selectError;
  }
  const freshCloud = normalizeAssignedQueue(freshRow?.assigned_skill_ids);
  const unseenAdditions = freshCloud.filter((id) => !known.includes(id));
  const idsToWrite = mergeCloudAssignedSkills(desired, unseenAdditions);

  const { data, error } = await client
    .from('students')
    .update({
      assigned_skill_ids: idsToWrite,
      assigned_skill_ids_updated_at: assignedSkillIdsUpdatedAt,
    })
    .eq('id', studentId)
    .or(`assigned_skill_ids_updated_at.is.null,assigned_skill_ids_updated_at.lte.${assignedSkillIdsUpdatedAt}`)
    .select()
    .maybeSingle();
  if (error) {
    const msg = String(error.message || '');
    // Pre-apply degradation: the stamp column doesn't exist yet. Fall back
    // to a plain, unguarded write of assigned_skill_ids alone — same
    // OPTIONAL_STUDENT_COLUMNS-style convention as updateStudent above.
    if (msg.includes("'assigned_skill_ids_updated_at'") || msg.includes('"assigned_skill_ids_updated_at"')) {
      const fallback = await client
        .from('students')
        .update({ assigned_skill_ids: idsToWrite })
        .eq('id', studentId)
        .select()
        .maybeSingle();
      if (fallback.error) {
        captureSupabaseError('updateAssignedSkillIdsSafe fallback write failed', fallback.error, { studentId });
        throw fallback.error;
      }
      return fallback.data;
    }
    captureSupabaseError('updateAssignedSkillIdsSafe failed', error, { studentId });
    throw error;
  }
  return data; // null = guard rejected (cloud already newer); row = written
}

// MA-395 (suppressed-skill-ids-cloud-sync slice) — guarded parent-device
// writer for students.suppressed_skill_ids. Deliberately the PLAIN
// stamp-guarded CAS shape (updateStudentLevelsSafe's pattern), NOT
// updateAssignedSkillIdsSafe's 3-step re-read/diff/re-union — that
// machinery exists solely to reconcile a teacher RPC's unstamped concurrent
// write, and no such second writer exists for suppressed_skill_ids.
//
// PRE-APPLY: unlike updateAssignedSkillIdsSafe/updateStudentLevelsSafe,
// there is no pre-existing base column to degrade onto (both
// suppressed_skill_ids and suppressed_skill_ids_updated_at are new in
// migration 0039) — a missing-column PostgREST error is NOT caught here; it
// propagates to the caller, which already ends every call site in a
// captureSupabaseError .catch (SkillTree.tsx's pushSuppressedSkillsToCloud,
// App.jsx's pull-side push block), so this never becomes an unhandled
// rejection — it just means suppression sync stays inert pre-apply (see
// 0039's migration header).
//
// Returns the written row, or null when the guard rejected the write (cloud
// already newer) or a no-op guard fired (missing studentId/stamp).
export async function updateSuppressedSkillIdsSafe(studentId, { suppressedSkillIds, suppressedSkillIdsUpdatedAt }, client = supabase) {
  if (!studentId || !suppressedSkillIdsUpdatedAt) return null
  const desired = normalizeSuppressedSet(suppressedSkillIds)
  const { data, error } = await client
    .from('students')
    .update({
      suppressed_skill_ids: desired,
      suppressed_skill_ids_updated_at: suppressedSkillIdsUpdatedAt,
    })
    .eq('id', studentId)
    .or(`suppressed_skill_ids_updated_at.is.null,suppressed_skill_ids_updated_at.lte.${suppressedSkillIdsUpdatedAt}`)
    .select()
    .maybeSingle()
  if (error) {
    captureSupabaseError('updateSuppressedSkillIdsSafe failed', error, { studentId })
    throw error
  }
  return data // null = guard rejected (cloud already newer); row = written
}

// Soft-delete a student: flips is_active=false so fetchStudents (which
// filters is_active=true) stops surfacing them. We never hard-DELETE —
// keeping the row preserves the kid's attempt/session history (and the
// FK targets that reference it) should the parent want them back. Callers
// fire-and-forget on the local child's supabaseStudentId, and ONLY when
// that id is non-null (offline kids have no cloud row to deactivate — see
// deleteChild in App.jsx).
//
// MA-393: routes through the deactivate_student RPC (migration 0032) so a
// DB trigger stamps a reason-carrying row into student_deactivation_events
// — the audit trail that was missing when the founder's own two production
// rows turned up deactivated with no record of who/when/why. `reason` is a
// short machine-readable string (e.g. "parent_deleted_via_settings"), not
// kid/parent-facing copy. Falls back to the old direct-update path (no
// audit row) on PGRST202 ("function not found") so the live Settings ->
// Kids -> Delete flow keeps working on a Supabase project that hasn't had
// 0032 applied yet.
export async function deactivateStudent(studentId, reason = 'unspecified', client = supabase) {
  if (!studentId) return null;
  const { error } = await client.rpc('deactivate_student', {
    p_student_id: studentId,
    p_reason: reason,
  });
  if (!error) return true;
  const msg = String(error.message || '');
  if (error.code === 'PGRST202' || /Could not find the function/i.test(msg)) {
    return updateStudent(studentId, { is_active: false }, client);
  }
  captureSupabaseError('deactivateStudent failed', error, { studentId });
  throw error;
}

// MA-629 slice b (docs/build/specs/MA-629b-name-collision-revive.md §2c) —
// revives a previously deactivated student row via the sanctioned
// reactivate_student RPC (migration 0052), the createStudent 23505-recovery
// path's ONLY caller today. Mirrors deactivateStudent's control flow exactly
// (RPC first, PGRST202/"function not found" -> direct update fallback so the
// flow works before and after the founder applies 0052) but NOT its return
// shape: deactivate_student RETURNS void, so deactivateStudent returns a bare
// `true` on success — reactivate_student RETURNS public.students, and BOTH
// arms here return the updated row, because createStudent's caller needs a
// real row back (a stale is_active:false object would otherwise sit in
// useCloudSync's in-memory students list until the next refreshStudents()).
//
// The RPC's success-arm data is unwrapped defensively —
// `Array.isArray(data) ? (data[0] ?? null) : data` — the same unwrap this
// tree already uses at its own TABLE-returning RPC call sites
// (src/lib/teacher/claimApi.ts:77, :124), because whether PostgREST hands
// back a non-setof composite as an object or a one-element array is decided
// server-side by `proretset` and is unverified against a live PostgREST from
// this sandbox; the unwrap is correct under either shape. The `?? null` on
// the array arm is load-bearing, not decorative: `data: []` (a real
// possibility if the RPC's own row-returning UPDATE/SELECT ever matched
// nothing) would otherwise make `data[0]` resolve to `undefined`, and
// `useCloudSync.js:287`'s `[s, ...prev.filter(p => p.id !== s.id)]` throws on
// `s.id` when `s` is `undefined` — caught by that caller's own try/catch,
// which sets `setCloudOffline(true)` and produces the exact silent
// local-only-child-plus-false-sync-toast outcome this slice exists to fix.
export async function reactivateStudent(studentId, reason = 'unspecified', client = supabase) {
  if (!studentId) return null;
  const { data, error } = await client.rpc('reactivate_student', {
    p_student_id: studentId,
    p_reason: reason,
  });
  if (!error) {
    return Array.isArray(data) ? (data[0] ?? null) : data;
  }
  const msg = String(error.message || '');
  if (error.code === 'PGRST202' || /Could not find the function/i.test(msg)) {
    // MA-629 slice b (ADV-5) — ⚠️ the ownership/family-scope belt §2c calls
    // load-bearing (`parent_id = auth.uid() AND enrollment_id IS NULL`) lives
    // ONLY inside the reactivate_student RPC (migration 0052). This fallback
    // is a bare `updateStudent(...)` -> `.update({is_active:true}).eq('id',
    // studentId)` with NO owner or family predicate of its own — it relies
    // entirely on RLS, and a kiosk shadow's `parent_id` is the TEACHER's own
    // `auth.uid()` (0042), so RLS alone would not stop this UPDATE from
    // reaching a shadow row IF one ever reached this call. Per §3, 0052 is
    // unapplied in prod today, so THIS is the only path that actually runs —
    // the sole thing standing between this call and a shadow row is the
    // client-side `.is('enrollment_id', null)` filter at createStudent's
    // lookup (§2a), which is why that filter is family scope for the WHOLE
    // recovery flow, not merely a lookup nicety. Do not add an ownership
    // predicate to `updateStudent` itself to close this — it is a shared
    // writer used by callers with different, already-correct authorization
    // shapes.
    return updateStudent(studentId, { is_active: true }, client);
  }
  captureSupabaseError('reactivateStudent failed', error, { studentId });
  throw error;
}

// ─── COPPA consent (MA-04) ──────────────────
// consent_records is keyed by parent_id (= auth.uid()); RLS restricts every
// row to its own parent. NOTE (FOUNDER-FYI): if prod is missing
// `GRANT SELECT, INSERT ON public.consent_records TO authenticated`, PostgREST
// returns 42501 — both helpers below fail SOFT (fetch → throws → the gate
// treats it as no-consent = local-only; record → caught + non-blocking), so the
// app never crashes, consent just doesn't persist until the GRANT is applied.

/** All consent rows for one parent. Throws on error (the consent gate catches
 *  and treats a read failure as fail-closed / no-consent). */
export async function fetchConsentRecords(parentId, client = supabase) {
  if (!parentId) return []
  const { data, error } = await client
    .from('consent_records')
    .select('*')
    .eq('parent_id', parentId)
  if (error) {
    captureSupabaseError('fetchConsentRecords failed', error, { parentId })
    throw error
  }
  return data || []
}

/** MA-554 — one structured console line per consent-save failure that does NOT
 *  reach the insert (and therefore never reaches captureSupabaseError).
 *
 *  ⚠️Console only, and deliberately so. The parent-facing toast copy is
 *  unchanged and must stay unchanged: "Couldn't save consent right now" is
 *  calm and says nothing a parent cannot act on. Raw internals — SQLSTATE
 *  codes, constraint names, session state — belong in a developer console and
 *  nowhere near a parent. This function is the seam that keeps those two
 *  audiences separate rather than collapsing them into one message.
 *
 *  Exported for tests: the bite injects a failing client, asserts the line is
 *  emitted naming the reason, and asserts a SUCCESSFUL save emits nothing. */
export function logConsentFailure(reason, extra = undefined) {
  console.error(`[consent] recordConsent failed: ${reason}`, extra ?? {})
}

/** Record a GRANTED COPPA consent for the current parent. Resolves parent_id
 *  from the live auth session (never from caller input), so dev/e2e (no real
 *  session) no-ops safely. Non-blocking: never throws — a failed insert leaves
 *  the kid local-only via the createCloudStudent gate.
 *  @param {{ method?: string, signature?: ({ name?: string, date?: string, acknowledged?: boolean, statement?: (string|null) }|null) }} [opts]
 *  @param {any} [client]
 */
export async function recordConsent({ method = VPC_METHOD, signature = null } = {}, client = supabase) {
  // MA-04 signed method (COPPA §312.5(b)(2)(i)): a granted row is written ONLY
  // when the signature is COMPLETE (typed legal name + date + the 16 CFR Part 312
  // acknowledgement). An incomplete attestation writes NOTHING — no granted row,
  // so the createCloudStudent gate stays closed and the kid stays local-only.
  // (We deliberately do NOT write a granted:false placeholder: nothing that could
  // be mistaken for consent reaches the table before the signature completes.)
  if (isSignedMethod(method) && !isCompleteSignature(signature)) {
    // MA-554 — this and the no-session return below used to be SILENT: they
    // exit before the insert, so captureSupabaseError never ran and the parent
    // saw the same generic toast as a database rejection with nothing at all
    // in the console to tell the two apart. Both now emit a structured line.
    // Parent-facing copy is untouched; this is console-only.
    logConsentFailure('incomplete-signature')
    return { ok: false, reason: 'incomplete-signature' }
  }
  let parentId = null
  try {
    const { data: { session } } = await client.auth.getSession()
    parentId = session?.user?.id ?? null
  } catch {
    parentId = null
  }
  if (!parentId) {
    logConsentFailure('no-session')
    return { ok: false, reason: 'no-session' }
  }
  const row = {
    parent_id: parentId,
    consent_type: buildConsentType(method),
    granted: true,
    granted_at: new Date().toISOString(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    // ip_address stays null: the browser cannot read its own public IP.
  }
  // Signed method: persist the auditable signed-form record (name/date/ack) in the
  // `signature` jsonb column (added by migration 0022 — founder-applies). The gate
  // (`isCompletedSignedConsent`) reads this back and refuses any row lacking it, so
  // a row written before this column exists in prod simply fails the signed gate
  // (fail-closed: local-only) rather than falsely opening it.
  if (isSignedMethod(method)) {
    row.signature = {
      name: String(signature.name).trim(),
      date: String(signature.date).trim(),
      acknowledged: true,
      statement: typeof signature.statement === 'string' ? signature.statement : null,
      method_version: 'coppa_v1_signed',
    }
  }
  const { error } = await client.from('consent_records').insert(row)
  if (error) {
    // MA-554 — the SQLSTATE goes in the MESSAGE, not only in the context
    // object. captureSupabaseError passes context as a separate console
    // argument, which a browser renders inspectably but a captured/flat log
    // line renders as "[object Object]" — so the one field a developer needs
    // to grep for would not be greppable. Verified by running it, not assumed.
    captureSupabaseError(`recordConsent failed (code=${error?.code ?? 'none'})`, error, {
      parentId,
      // MA-554 — name the PostgREST/Postgres fields that actually identify the
      // cause. The 2026-08-23 incident (a manufactured account with a missing
      // profile row) burned three debugging rounds before SQL-side inspection
      // found it, because the only signal reaching anyone was a generic toast.
      // `code` is the SQLSTATE (23503 = foreign_key_violation, 42501 = RLS
      // policy rejection — the two that produced that incident), and `details`
      // / `hint` carry the constraint name when Postgres supplies one.
      code: error?.code ?? null,
      details: error?.details ?? null,
      hint: error?.hint ?? null,
    })
    return { ok: false, reason: 'insert-error', code: error?.code ?? null }
  }
  return { ok: true, parentId }
}

/** MA-311 — read the current session's parent's weekly-recap opt-in state
 *  from `notif_pref`. Resolves parent_id from the live auth session (never
 *  from caller input, matching `recordConsent`). A parent with no row yet
 *  (never opted in) reads as `enabled: false` — this IS the default-off
 *  state; the cron's recipient gate (`weekly-recap.js`) only selects rows
 *  where `email_weekly_recap=true`, so an absent row is already correctly
 *  excluded.
 *  @param {any} [client]
 */
export async function fetchEmailWeeklyRecapEnabled(client = supabase) {
  let parentId = null
  try {
    const { data: { session } } = await client.auth.getSession()
    parentId = session?.user?.id ?? null
  } catch {
    parentId = null
  }
  if (!parentId) return { ok: false, reason: 'no-session', enabled: false }
  const { data, error } = await client
    .from('notif_pref')
    .select('email_weekly_recap')
    .eq('parent_id', parentId)
    .maybeSingle()
  if (error) {
    captureSupabaseError('fetchEmailWeeklyRecapEnabled failed', error, { parentId })
    return { ok: false, reason: 'select-error', enabled: false }
  }
  return { ok: true, parentId, enabled: !!data?.email_weekly_recap }
}

/** MA-311 — write the current session's parent's weekly-recap opt-in state.
 *  Resolves parent_id from the live auth session (never from caller input).
 *  Narrow upsert (only `parent_id`/`email_weekly_recap`) so an existing
 *  row's `unsubscribe_token` is never touched. Never throws — a failed
 *  write is reported to Sentry and the caller shows a toast; toggling this
 *  never sends an email itself (the cron reads the row on its own schedule).
 *  @param {boolean} enabled
 *  @param {any} [client]
 */
export async function setEmailWeeklyRecap(enabled, client = supabase) {
  let parentId = null
  try {
    const { data: { session } } = await client.auth.getSession()
    parentId = session?.user?.id ?? null
  } catch {
    parentId = null
  }
  if (!parentId) return { ok: false, reason: 'no-session' }
  const { error } = await client
    .from('notif_pref')
    .upsert({ parent_id: parentId, email_weekly_recap: !!enabled }, { onConflict: 'parent_id' })
  if (error) {
    captureSupabaseError('setEmailWeeklyRecap failed', error, { parentId })
    return { ok: false, reason: 'upsert-error' }
  }
  return { ok: true, parentId, enabled: !!enabled }
}

/** MA-464 — record the ACCOUNT-scoped school-authority-direct consent a
 *  teacher-role account attests before creating a student (classroom-first
 *  model, MA-DEC-0802-WS8). Mirrors recordConsent's signed-form discipline:
 *  an INCOMPLETE attestation (missing typed name, date, or the acknowledgement)
 *  writes NOTHING, so the createCloudStudent gate stays closed and the kid
 *  stays local-only (fail-closed). Distinct from recordSchoolAuthorityConsent
 *  below, which is the PER-CLASS roster-entry ack (school_authority_v1:<classId>).
 *  @param {{ name?: string, date?: string, statement?: (string|null) }} [attestation]
 *  @param {any} [client]
 */
export async function recordSchoolAuthorityDirectConsent({ name, date, statement = null } = {}, client = supabase) {
  const attestation = { name: String(name ?? ''), date: String(date ?? ''), acknowledged: true }
  if (!isCompleteSignature(attestation)) {
    return { ok: false, reason: 'incomplete-attestation' }
  }
  let parentId = null
  try {
    const { data: { session } } = await client.auth.getSession()
    parentId = session?.user?.id ?? null
  } catch {
    parentId = null
  }
  if (!parentId) return { ok: false, reason: 'no-session' }
  const row = {
    parent_id: parentId,
    consent_type: SCHOOL_DIRECT_CONSENT_TYPE,
    granted: true,
    granted_at: new Date().toISOString(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    signature: {
      name: String(name).trim(),
      date: String(date).trim(),
      acknowledged: true,
      statement: typeof statement === 'string' ? statement : null,
      method_version: 'school_authority_direct_v1',
    },
  }
  const { error } = await client.from('consent_records').insert(row)
  if (error) {
    captureSupabaseError('recordSchoolAuthorityDirectConsent failed', error, { parentId })
    return { ok: false, reason: 'insert-error' }
  }
  return { ok: true, parentId }
}

/** Record a GRANTED school-authority consent (MA-60 lane b) for the current
 *  teacher, scoped to one class. Resolves parent_id (the teacher's own
 *  `parents.id`) from the live auth session (never from caller input), so
 *  dev/e2e (no real session) no-ops safely. Non-blocking: never throws — a
 *  failed insert leaves the roster-add gate closed via `runGatedRosterWrite`.
 *  @param {string} classId
 *  @param {any} [client]
 */
export async function recordSchoolAuthorityConsent(classId, client = supabase) {
  let parentId = null
  try {
    const { data: { session } } = await client.auth.getSession()
    parentId = session?.user?.id ?? null
  } catch {
    parentId = null
  }
  if (!parentId) return { ok: false, reason: 'no-session' }
  const row = {
    parent_id: parentId,
    consent_type: schoolConsentType(classId),
    granted: true,
    granted_at: new Date().toISOString(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  }
  const { error } = await client.from('consent_records').insert(row)
  if (error) {
    captureSupabaseError('recordSchoolAuthorityConsent failed', error, { parentId, classId })
    return { ok: false, reason: 'insert-error' }
  }
  return { ok: true, parentId }
}

/** Record a GRANTED receipt-view consent (MA-61 teacher-receipt-view slice)
 *  for the current teacher, scoped to one student. Mirrors
 *  recordSchoolAuthorityConsent exactly (resolves parent_id from the live
 *  session, never throws — a failed insert leaves the receipt gate closed
 *  via guardReceiptView).
 *  @param {string} studentId
 *  @param {any} [client]
 */
export async function recordReceiptViewConsent(studentId, client = supabase) {
  let parentId = null
  try {
    const { data: { session } } = await client.auth.getSession()
    parentId = session?.user?.id ?? null
  } catch {
    parentId = null
  }
  if (!parentId) return { ok: false, reason: 'no-session' }
  const row = {
    parent_id: parentId,
    consent_type: receiptConsentType(studentId),
    granted: true,
    granted_at: new Date().toISOString(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  }
  const { error } = await client.from('consent_records').insert(row)
  if (error) {
    captureSupabaseError('recordReceiptViewConsent failed', error, { parentId, studentId })
    return { ok: false, reason: 'insert-error' }
  }
  return { ok: true, parentId }
}

/** Record a GRANTED assign consent (MA-61 assign slice) for the current
 *  teacher, scoped to one student. Mirrors recordReceiptViewConsent exactly
 *  (resolves parent_id from the live session, never throws — a failed
 *  insert leaves the assign gate closed via guardAssignSkillWrite).
 *  @param {string} studentId
 *  @param {any} [client]
 */
export async function recordAssignConsent(studentId, client = supabase) {
  let parentId = null
  try {
    const { data: { session } } = await client.auth.getSession()
    parentId = session?.user?.id ?? null
  } catch {
    parentId = null
  }
  if (!parentId) return { ok: false, reason: 'no-session' }
  const row = {
    parent_id: parentId,
    consent_type: assignConsentType(studentId),
    granted: true,
    granted_at: new Date().toISOString(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  }
  const { error } = await client.from('consent_records').insert(row)
  if (error) {
    captureSupabaseError('recordAssignConsent failed', error, { parentId, studentId })
    return { ok: false, reason: 'insert-error' }
  }
  return { ok: true, parentId }
}

/** MA-937 (D-TUX-25) — the teacher's class-level acknowledgement
 *  (`class_practice_v1:<classId>`, src/App/receiptConsentGate.js), written by
 *  the teacher's own session through the same consent_records insert as
 *  recordSchoolAuthorityConsent (existing table + RLS, no migration). */
export async function recordClassPracticeConsent(classId, client = supabase) {
  let parentId = null
  try {
    const { data: { session } } = await client.auth.getSession()
    parentId = session?.user?.id ?? null
  } catch {
    parentId = null
  }
  if (!parentId) return { ok: false, reason: 'no-session' }
  const row = {
    parent_id: parentId,
    consent_type: classPracticeConsentType(classId),
    granted: true,
    granted_at: new Date().toISOString(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
  }
  const { error } = await client.from('consent_records').insert(row)
  if (error) {
    captureSupabaseError('recordClassPracticeConsent failed', error, { parentId, classId })
    return { ok: false, reason: 'insert-error' }
  }
  return { ok: true, parentId }
}

// ─── Session (practice) helpers ─────────────

/** MA-377: deterministic idempotency key for a `sessions` row.
 *  `studentId:runId` — same run retried (double-finalize race, a stale
 *  cloud-write retry after reconnect) lands ONE row via the non-partial
 *  unique index (migration 0037, the 0015/0017 MA-144 lesson). `runId`
 *  is optional (legacy `useCloudSync.saveCloudSession` caller passes
 *  none) — a null event_key never conflicts under Postgres' NULLS
 *  DISTINCT default, so an unkeyed caller degrades to pre-existing
 *  insert-like behavior rather than erroring. */
export function sessionEventKey(studentId, runId) {
  return studentId && runId ? `${studentId}:${runId}` : null
}

/** @param {any} session
 *  @param {any} [client] injectable for tests (mirrors attemptLogger's
 *  client-param seam); defaults to the real supabase client. */
export async function saveSession(session, client = supabase) {
  // MA-377: skip the network while the breaker is open — retrying now
  // would just re-hit a dead backend. THROW (not swallow) so every
  // caller's existing `try { await saveSession(...) } catch {...}`
  // (4 session screens) and the new saveSessionWithOutbox wrapper see a
  // uniform failure signal and can queue the row for reconnect — a
  // silent return here would make the row vanish with no trace and no
  // outbox entry.
  if (isCircuitOpen()) {
    throw new Error('saveSession: circuit breaker open')
  }
  // P-TIER3-INSTRUMENTATION-A — stamp ambient session metadata for the
  // parent-dashboard foundation. Read from navigator + build env here so
  // BOTH writers (legacy useCloudSync.saveCloudSession + the new TS
  // finalizeSession in *Session.tsx) get the fields without a screen edit.
  const ambient = ambientSessionMetadata()
  // started_at is derivable: the row is INSERTed at session end, so
  // started = end − duration. Only when a duration was passed (the legacy
  // path passes none → null, matching the nullable column).
  const startedAt =
    typeof session.durationMs === 'number' && session.durationMs >= 0
      ? new Date(Date.now() - session.durationMs).toISOString()
      : null
  const row = {
    student_id: session.studentId,
    subject: session.subject,
    level_id: session.levelId,
    correct: session.correct,
    total: session.total,
    feeling: session.feeling || null,
    duration_ms: session.durationMs || null,
    xp_earned: session.xpEarned || 0,
    results: session.results || [],
    // ── ambient metadata (0008) ──────────────────────────────────────
    // device_type + app_version are 0000-baseline columns that were
    // never written until now; the other six are 0008 additions.
    device_type: ambient.deviceType,
    app_version: ambient.appVersion,
    content_version: ambient.contentVersion,
    locale: ambient.locale,
    os: ambient.os,
    offline_flag: ambient.offlineFlag,
    started_at: startedAt,
    // saveSession only fires at the recap (completed) in both paths.
    // completion_state stays 'completed' (E1b decision: exited/timed_out/
    // abandoned are derived on read from the absence of a session_completed
    // milestone — see migration 0009 COMMENT — to keep "Exit is pure
    // navigation" intact). The ?? still lets a future caller pass a state.
    completion_state: session.completionState ?? 'completed',
    session_source: session.sessionSource ?? 'path',
    // ── P-TIER3-INSTRUMENTATION-B lifecycle effort fields (0009) ──────
    // WIRED — the TS finalizeSession path passes these from the screen's
    // lifecycle hook. The legacy JSX path omits them → column DEFAULT 0.
    // pause_ms is a STUB (no pause UI); it rides the column DEFAULT 0 even
    // when omitted, and is passed through here if a caller ever supplies it.
    active_ms: session.activeMs ?? 0,
    idle_ms: session.idleMs ?? 0,
    pause_ms: session.pauseMs ?? 0,
    app_backgrounded_count: session.appBackgroundedCount ?? 0,
    // ── MA-377: idempotency key (migration 0037) ──────────────────────
    run_id: session.runId ?? null,
    event_key: sessionEventKey(session.studentId, session.runId),
  }
  let lastError = null
  for (let attempt = 0; attempt <= SESSION_MAX_RETRIES; attempt++) {
    // MA-377: upsert + ignoreDuplicates (ON CONFLICT DO NOTHING) so a
    // retried write for the same event_key is a safe no-op — mirrors
    // logAttempt's proven pattern. No `.select()`: `ignoreDuplicates`
    // returns 0 rows on a dedup-skip, and `.single()` would then throw
    // PGRST116, turning a SUCCESSFUL dedup into a reported failure. No
    // caller reads saveSession's return value (verified — all 5
    // producers discard it), so dropping the return value is safe.
    const { error } = await client.from('sessions').upsert(row, {
      onConflict: 'event_key',
      ignoreDuplicates: true,
    })
    if (!error) {
      recordSuccess()
      return
    }
    lastError = error
    if (attempt < SESSION_MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, SESSION_RETRY_BASE_MS * (attempt + 1)))
    }
  }
  recordFailure()
  // captureSupabaseError keeps the existing supabase-area trail;
  // captureMetadataWriteError adds the metadata-write breadcrumb so a
  // dropped dashboard-foundation write is filterable + leaves a trail.
  captureSupabaseError('saveSession failed', lastError, { studentId: session.studentId, subject: session.subject })
  captureMetadataWriteError('saveSession metadata write failed', lastError, {
    studentId: session.studentId,
    subject: session.subject,
    fields: ['device_type', 'app_version', 'content_version', 'locale', 'os', 'offline_flag', 'started_at', 'completion_state', 'session_source', 'active_ms', 'idle_ms', 'pause_ms', 'app_backgrounded_count', 'run_id', 'event_key'],
  })
  throw lastError
}

export async function fetchRecentSessions(studentId, limit = 20) {
  const { data, error } = await supabase
    .from('sessions')
    .select('*')
    .eq('student_id', studentId)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data || []
}

// ─── Daily log helpers ──────────────────────

export async function upsertDailyLog(studentId, subject, client = supabase) {
  const today = new Date().toISOString().slice(0, 10)

  // True upsert using the (student_id, date) unique constraint added by
  // migration 0013 (daily_log_student_date_unique; also present in the 0000
  // baseline as daily_log_student_id_date_key). onConflict targets the
  // column names that back the constraint. ignoreDuplicates: false so a
  // conflict triggers a merge, not a discard. This replaces the old
  // SELECT-then-UPDATE/INSERT pattern which had a TOCTOU race: two concurrent
  // session-ends for the same kid+day both selected 0 rows, then both
  // inserted, producing a duplicate daily_log row (and a PostgREST 23505
  // on the second). With a true upsert only one row ever exists per
  // (student_id, date); the count columns increment atomically.
  //
  // supabase-js v2 upsert shape: .upsert(row, { onConflict: 'col1,col2' })
  // On INSERT the row is created with subject_sessions = 1.
  // On conflict the existing row is replaced with the new values — so we
  // must first fetch the existing count to increment it, OR use the
  // SELECT-then-upsert pattern here. We keep the SELECT so the increment
  // is correct, but replace the fragile INSERT/UPDATE split with a single
  // upsert that is safe to retry.
  const { data: existing } = await client
    .from('daily_log')
    .select('*')
    .eq('student_id', studentId)
    .eq('date', today)
    .maybeSingle()

  const sessionField = `${subject}_sessions`
  const newCount = ((existing && existing[sessionField]) || 0) + 1

  // Narrow the upsert payload to ONLY the conflict keys + the one subject
  // column being incremented. Spreading `...existing` would, on the ON
  // CONFLICT DO UPDATE path, write back EVERY column — clobbering a concurrent
  // increment to a DIFFERENT subject (e.g. reading) that landed between our
  // SELECT and UPSERT. (A same-subject concurrent increment can still lose one
  // count — the fully atomic fix is a SQL-side increment RPC, deferred; the
  // 0013 (student_id,date) unique constraint already prevents duplicate ROWS,
  // which was the actual incident class.)
  const row = { student_id: studentId, date: today, [sessionField]: newCount }

  const { data, error } = await client
    .from('daily_log')
    .upsert(row, { onConflict: 'student_id,date' })
    .select()
    .single()
  if (error) throw error
  return data
}

// NOTE: the former `syncProgressToCloud` / `loadProgressFromCloud` helpers
// (a dead, never-called write/read pair) were removed in ADR-025 / MA-40.
// They blind-wrote streak/last_practice_date/levels against an OBSOLETE
// progress vocabulary (`progress.mathLevelId`/`longestStreak`/`totalXp` —
// none of which exist on the live progress object, which uses
// `mathModuleId`/`readingModuleId` and has no `longestStreak`/`totalXp`), so
// wiring them up would have silently written undefined values AND reopened
// the device-hop progress-regression bug (a stale device clobbering a newer
// device's streak/level). The live read path is `crossDeviceHydration.js`;
// the live write path is `useCloudSync.syncToCloud`. The real device-hop
// fix is tracked as MA-39 (cloud-sync workstream) — build it on a recency
// guard, not by resurrecting these helpers.

// ─── Parent-row guarantee (MA-02) ────────────────────────────────────────
//
// Defense-in-depth behind the on_auth_user_created trigger. The trigger is
// confirmed live in prod and fires at signup, but a transient DB error in the
// trigger's exception block silently leaves the user with no parents row
// (raise log goes to server logs only — no observable signal). This upsert
// fires on boot + SIGNED_IN and ensures the row exists. ON CONFLICT (id)
// DO NOTHING so a trigger-created row (or one the parent edited) is never
// overwritten. Non-blocking: the app still works if this fails.
//
// MA-23: `.select()` on the upsert causes PostgreSQL's RETURNING clause to
// emit the newly-inserted row only (ON CONFLICT DO NOTHING returns nothing).
// data.length > 0 therefore reliably signals "first signup" vs "returning
// user" without an extra query or a schema migration. fetchFn is injected for
// testability; defaults to globalThis.fetch in production.
// MA-1205: the welcome email's subject and HTML are built server-side now
// (api/_lib/accountEmailCopy.js) and go only to the signed-in account's own
// address; the client sends { kind: 'welcome' } with the session's access token.
function sendWelcomeEmail(client, fetchFn) {
  Promise.resolve()
    .then(async () => {
      const { data } = await client.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) return;
      await fetchFn('/api/send-email', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ kind: 'welcome' }),
      });
    })
    .catch(() => {});
}

export async function ensureParentRow(user, client = supabase, fetchFn = globalThis.fetch) {
  if (!user?.id) return false;
  const timezone =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'America/Los_Angeles';
  const { data, error } = await client
    .from('parents')
    .upsert(
      {
        id:           user.id,
        email:        user.email ?? null,
        display_name: user.user_metadata?.full_name ?? null,
        timezone,
      },
      { onConflict: 'id', ignoreDuplicates: true },
    )
    .select();
  if (error) {
    captureSupabaseError('ensureParentRow failed', error, { userId: user.id });
    return false;
  }
  // Fire welcome email only on confirmed first insert; returning users get
  // data = [] from ON CONFLICT DO NOTHING, so the email never double-sends.
  // Fully fire-and-forget — never blocks the auth render path.
  const isNewRow = Array.isArray(data) && data.length > 0;
  if (isNewRow && user.email) {
    sendWelcomeEmail(client, fetchFn);
  }
  return true;
}

// ─── Track I.7: cross-device placement + parent-priors sync ─────────────
//
// These three helpers persist + restore the placement seed and parent
// ability-priors so they survive a device change. The local store stays the
// source of truth; the cloud is the durable mirror (fire-and-forget writes).
//
// Each takes an optional `client = supabase` last param — a DI seam so unit
// tests can pass a stub and assert the issued .from()/.update()/.insert()/
// .select() call shape (the wrappers are too thin to have a pure mapping
// layer like attemptLogger's toRow/fromRow). Default keeps call-sites
// unchanged + zero runtime cost.

// Mirror local progress.parentPriors → students.parent_priors. Plain
// .update() — deliberately does NOT use updateStudent's OPTIONAL_STUDENT_
// COLUMNS fallback loop (parent_priors is a required-present column post-0012,
// not an optional one). Fire-and-forget: returns true on success, false on
// failure (local write already happened; we don't throw or block the UI).
export async function upsertStudentParentPriors(studentId, parentPriors, client = supabase) {
  if (!studentId) return false
  const { error } = await client
    .from('students')
    .update({ parent_priors: parentPriors ?? {} })
    .eq('id', studentId)
  if (error) {
    captureSupabaseError('upsertStudentParentPriors failed', error, { studentId })
    return false
  }
  return true
}

// Append one placement-diagnostic outcome row. Called by I.1.a's diagnostic
// finish (this PR ships the function; I.1.a wires the call). Append-only.
export async function insertPlacementResult({ studentId, result, source = 'onboarding_diagnostic' }, client = supabase) {
  if (!studentId) return false
  const { error } = await client
    .from('placement_results')
    .insert({ student_id: studentId, result: result ?? {}, source })
  if (error) {
    captureSupabaseError('insertPlacementResult failed', error, { studentId, source })
    return false
  }
  return true
}

// Read the newest placement result for a student (or null). Consumer is
// I.1.b (engine initial-difficulty seed) — ships tested-but-unconsumed here.
export async function fetchLatestPlacementResult(studentId, client = supabase) {
  if (!studentId) return null
  const { data, error } = await client
    .from('placement_results')
    .select('*')
    .eq('student_id', studentId)
    .order('created_at', { ascending: false })
    .limit(1)
  if (error) {
    captureSupabaseError('fetchLatestPlacementResult failed', error, { studentId })
    return null
  }
  return (data && data[0]) || null
}

// MA-784 — the newest few placement-result rows for a student (newest first).
// The onboarding diagnostic appends TWO rows (math, then reading — the
// strand is the row's `result.source`), so the single-row read above cannot
// restore both strands. Same table, same columns, same RLS select policy
// (migration 0012): NO schema change. Returns [] on any failure (callers
// fail safe to the legacy provenance rule).
export async function fetchPlacementResultsForStudent(studentId, limit = 12, client = supabase) {
  if (!studentId) return []
  const { data, error } = await client
    .from('placement_results')
    .select('result, source, created_at')
    .eq('student_id', studentId)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) {
    captureSupabaseError('fetchPlacementResultsForStudent failed', error, { studentId })
    return []
  }
  return Array.isArray(data) ? data : []
}
